import React, { useState, useEffect } from 'react';
import { useFormikContext } from 'formik';
import { Col, Label, Row } from 'reactstrap';
import { StyledTable } from 'components/Table/StyledTable';
import { BlueLink } from 'components/Common';
import { useModal } from 'hooks/useModal';
import { ModalOrderTypeDocumentCondition } from 'components/OrderType/ModalOrderTypeDocumentCondition';
import { useTranslation } from 'react-i18next';
import { ShadowCard } from 'components/Common';
import { showToastError } from 'helpers/utils/toast';

export const OrderTypeEditDocuments = ({
  orderTypeData,
  attached,
  available,
  country = null,
  visa = null,
}) => {
  const { t } = useTranslation();
  const { handleOpenModal, handleCloseModal, isOpen } = useModal();
  const [attachedDocuments, setAttAchedDocuments] = useState([]);
  const [availableDocuments, setAvailableDocuments] = useState([]);

  const formik = useFormikContext();

  useEffect(() => {
    setAttAchedDocuments(
      attached &&
        attached
          .sort((a, b) => a.position - b.position)
          .map(doc => ({
            id: doc.id,
            position: doc.position,
            translations: doc.translations,
            conditions: doc.conditions?.map((cond, index) => ({
              // ...cond,
              country: cond.country,

              description_requirements_de: cond.description_requirements_de,
              description_requirements_en: cond.description_requirements_en,
              description_source_de: cond.description_source_de,
              description_source_en: cond.description_source_en,
              description_title_de: cond.description_title_de,
              description_title_en: cond.description_title_en,

              document: cond.document,
              examplePreview: cond.example,
              if_not_product: cond.if_not_product,
              if_product: cond.if_product,

              id: cond.id,
              index: index + 1,
              is_individual: cond.is_individual,
              is_legal: cond.is_legal,
              order_type: cond.order_type,
              previous_visa: cond.previous_visa,
              questionnairePreview: cond.questionnaire,

              require_original: cond.require_original,
              require_residency: cond.require_residency,
              valid_nationalities: cond.valid_nationalities,
              visa_duration: cond.visa_duration,
              visa_type: cond.visa_type,
              visum_integration: cond.visum_integration,
            })),
          })),
    );
  }, [attached]);

  useEffect(() => {
    setAvailableDocuments(
      available.sort((a, b) =>
        a.translations.en.toLowerCase() > b.translations.en.toLowerCase()
          ? 1
          : -1,
      ),
    );
  }, [available]);

  useEffect(() => {
    formik.setFieldValue('documents', attachedDocuments);
  }, [attachedDocuments]);

  const deleteCondition = (docId, condIndex) => {
    const doc = attachedDocuments.find(doc => doc.id === docId);

    const attachedDocument = {
      id: doc.id,
      position: doc.position,
      translations: doc.translations,
      conditions: doc.conditions.filter(cond => cond.index !== condIndex),
    };

    let docs = attachedDocuments.filter(doc => doc.id !== docId);

    if (attachedDocument.conditions?.length >= 1) {
      docs.push(attachedDocument);
    } else {
      setAvailableDocuments(
        [
          ...availableDocuments,
          {
            id: attachedDocument.id,
            translations: attachedDocument.translations,
          },
        ].sort((a, b) =>
          a.translations.en.toLowerCase() > b.translations.en.toLowerCase()
            ? 1
            : -1,
        ),
      );
    }
    setAttAchedDocuments(docs.sort((a, b) => a.position - b.position));
  };

  const deleteDocument = docId => {
    const doc = attachedDocuments.find(doc => doc.id == docId);

    setAvailableDocuments(
      [
        ...availableDocuments,
        {
          id: doc.id,
          translations: doc.translations,
        },
      ].sort((a, b) =>
        a.translations.en.toLowerCase() > b.translations.en.toLowerCase()
          ? 1
          : -1,
      ),
    );
    setAttAchedDocuments(
      attachedDocuments
        .filter(doc => doc.id !== docId)
        .sort((a, b) => a.position - b.position),
    );
  };

  const addDocument = docId => {
    const doc = availableDocuments.find(doc => doc.id == docId);
    setAvailableDocuments(availableDocuments.filter(doc => doc.id != docId));

    setAttAchedDocuments(
      [
        ...attachedDocuments,
        {
          id: doc.id,
          position: attachedDocuments.length, // leng is position + 1
          translations: doc.translations,
          conditions: [],
        },
      ].sort((a, b) => a.position - b.position),
    );

    handleOpenModal({
      order_type: formik.values.id,
      document: doc,
    });
  };

  const closeModal = e => {
    handleCloseModal();
    const doc = attachedDocuments.find(doc => doc.id == isOpen.document.id);

    if (!!e && doc?.conditions.length === 0) {
      deleteDocument(isOpen.document.id);
    }
  };

  const moveUp = position => {
    if (position == 0) {
      showToastError('Already on top!');
      return;
    }

    const prev_position = position - 1;
    const current = attachedDocuments.find(doc => doc.position == position);
    const prev = attachedDocuments.find(doc => doc.position == prev_position);

    let docs = [
      ...attachedDocuments.filter(
        doc => doc.position !== position && doc.position !== prev_position,
      ),
      {
        ...current,
        position: prev_position,
      },
      {
        ...prev,
        position: position,
      },
    ].sort((a, b) => a.position - b.position);
    setAttAchedDocuments(docs);
  };

  const moveDown = position => {
    if (position + 1 == attachedDocuments.length) {
      showToastError('Already last!');
      return;
    }

    const next_position = position + 1;
    const current = attachedDocuments.find(doc => doc.position == position);
    const next = attachedDocuments.find(doc => doc.position == next_position);

    let docs = [
      ...attachedDocuments.filter(
        doc => doc.position !== position && doc.position !== next_position,
      ),
      {
        ...next,
        position: position,
      },
      {
        ...current,
        position: next_position,
      },
    ].sort((a, b) => a.position - b.position);
    setAttAchedDocuments(docs);
  };

  return (
    <>
      {/* Attached docs */}
      {attachedDocuments?.map((document, documentIndex) => (
        <ShadowCard key={documentIndex} className="mt-4">
          <div
            key={documentIndex}
            className="d-flex flex-wrap alert alert-info"
          >
            <Col xs={11} className="p-0 d-flex align-items-center">
              <Label className="m-0 display-5">
                {documentIndex + 1}. {document.translations['en']} (
                {document.translations['de']})
              </Label>
            </Col>
            <Col xs={1}>
              <i
                className="bx bx-up-arrow"
                style={{ cursor: 'pointer' }}
                onClick={() => moveUp(document.position)}
              />
              <i
                className="bx bx-down-arrow"
                style={{ cursor: 'pointer' }}
                onClick={() => moveDown(document.position)}
              />
            </Col>
          </div>

          <StyledTable className="mb-5 ">
            <thead>
              <tr>
                <th />
                {!!visa && (
                  <>
                    <th>{t('orderTypes.visaType')}</th>
                    <th>{t('orderTypes.visaDuration')}</th>
                  </>
                )}

                <th>{t('orderTypes.requireOriginal')}</th>
                <th>{t('orderTypes.ifService')}</th>
                <th>{t('orderTypes.ifNotService')}</th>
                <th>{t('orderTypes.example')}</th>
                <th>{t('orderTypes.questionnaire')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {document.conditions.map((condition, conditionIndex) => (
                <tr key={condition.condition?.id}>
                  <td>
                    <BlueLink
                      to="#"
                      className="fw-semibold"
                      style={{ fontSize: '14px' }}
                      onClick={() => handleOpenModal({ document, condition })}
                    >
                      #{conditionIndex + 1}.&nbsp;
                    </BlueLink>
                  </td>
                  {!!visa && (
                    <>
                      <td>{visa.name}</td>
                      <td>{condition?.visa_duration?.label}</td>
                    </>
                  )}
                  <td>{condition.require_original ? 'Yes' : 'No'}</td>
                  <td>{condition.if_product?.label}</td>
                  <td>{condition.if_not_product?.label}</td>
                  <td>
                    {condition.examplePreview && (
                      <a
                        href={`${process.env.REACT_APP_API_URL}${condition.examplePreview}`}
                      >
                        <i
                          className="bx bxs-file-image"
                          style={{ fontSize: '20px' }}
                        />
                      </a>
                    )}
                  </td>
                  <td>
                    {condition.questionnairePreview && (
                      <a
                        href={`${process.env.REACT_APP_API_URL}${condition.questionnairePreview}`}
                      >
                        <i
                          className="bx bxs-file-pdf"
                          style={{ fontSize: '20px' }}
                        />
                      </a>
                    )}
                  </td>
                  <td>
                    <i
                      style={{ color: 'red', cursor: 'pointer' }}
                      className="bx bx-trash-alt"
                      onClick={() =>
                        deleteCondition(document.id, condition.index)
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
            <a
              key={documentIndex}
              style={{ fontSize: '12px' }}
              className="mt-2 d-flex flex-nowrap align-items-center"
              onClick={() => handleOpenModal({ document })}
            >
              <i className="bx bx-plus fs-4 me-2" />
              Add {document.conditions.length > 0 ? 'another' : ''} condition
              for {document.translations['en']}
            </a>
          </StyledTable>
        </ShadowCard>
      ))}

      {/* Available docs */}

      {availableDocuments?.length > 0 && (
        <>
          <Row className="d-flex m-3 mt-5 flex-wrap">
            <Col className="p-0 d-flex align-items-center">
              <Label className="m-0 display-5">
                {t('orderTypes.availableDocuments')}
              </Label>
            </Col>
          </Row>

          <StyledTable>
            <thead>
              <tr>
                <th>EN</th>
                <th>DE</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {availableDocuments?.map((document, availableIndex) => (
                <tr key={document.id}>
                  <td>
                    {availableIndex + 1}. {document.translations['en']}
                  </td>
                  <td>{document.translations['de']}</td>
                  <td>
                    <i
                      title={`add "${document.translations['en']}" to attached documents`}
                      className="bx bx-plus"
                      style={{ color: 'blue', cursor: 'pointer' }}
                      onClick={() => addDocument(document.id)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </StyledTable>
        </>
      )}

      {isOpen && (
        <ModalOrderTypeDocumentCondition
          handleCloseModal={closeModal}
          isOpenModal={isOpen}
          orderTypeData={orderTypeData}
          props={isOpen}
          country={country}
          visa={visa}
          attachedDocuments={attachedDocuments}
          availableDocuments={availableDocuments}
          setAttAchedDocuments={setAttAchedDocuments}
          setAvailableDocuments={setAvailableDocuments}
        />
      )}
    </>
  );
};
